/**
 * The deals of one person or organization, of every status, on its detail
 * screen. Paged like the deals list, newest first.
 */
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import type { DealDto } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { EntityTable } from '../../components/EntityTable';
import { useAsync } from '../../hooks/useAsync';
import { useListQuery } from '../../hooks/useListQuery';
import { listDeals } from './api';
import { DealStageOrStatus } from './DealStatus';
import { ConvertedValue } from './ConvertedValue';
import { formatMoney } from './format';

export function RelatedDeals({ filter }: { filter: { personId: number } | { organizationId: number } }) {
  const { t } = useTranslation('crm');
  const key = JSON.stringify(filter);
  const list = useListQuery({ defaultSort: 'createdAt', defaultOrder: 'desc', defaultLimit: 10, resetKey: key });
  const deals = useAsync(() => listDeals({ ...filter, ...list.query, status: 'all' }), [key, list.key]);

  return (
    <div>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        {t('deals.title')}
      </Typography>
      <AsyncContent state={deals}>
        {({ deals: rows, page }) => (
          <EntityTable<DealDto>
            rows={rows}
            rowKey={(deal) => deal.id}
            list={list}
            page={page}
            empty={{ icon: <HandshakeOutlinedIcon fontSize="inherit" />, title: t('deals.noneHere'), dense: true }}
            columns={[
              {
                field: 'title',
                label: t('field.title'),
                sortable: true,
                render: (deal) => (
                  <Link component={RouterLink} to={`/crm/deals/${deal.id}`} underline="hover" sx={{ fontWeight: 500 }}>
                    {deal.title}
                  </Link>
                ),
              },
              {
                field: 'stage',
                label: t('field.stage'),
                render: (deal) => <DealStageOrStatus deal={deal} />,
              },
              {
                field: 'value',
                label: t('field.value'),
                sortable: true,
                align: 'right',
                render: (deal) => (
                  <>
                    {formatMoney(deal.value, deal.currency)}
                    <ConvertedValue converted={deal.converted} />
                  </>
                ),
              },
            ]}
          />
        )}
      </AsyncContent>
    </div>
  );
}
